import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, Check, Share2, Gift, Users, ShieldCheck, Wallet, TrendingUp, Trophy, Award, Sparkles, ArrowRight, MessageCircle, Building2, Crown, Zap, Rocket, Send, Facebook, Instagram, Presentation as PresentationIcon, FileDown, X, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatINR } from "../lib/api";
import { toast } from "sonner";
import Seo from "../components/Seo";

const HOW_STEPS = [
    { icon: Share2, title: "Share your code", body: "Send your unique referral link via WhatsApp, Telegram, Facebook or Instagram." },
    { icon: Users, title: "They sign up", body: "Instant ₹200 bonus credit when someone joins using your code (usable on subscription plans)." },
    { icon: ShieldCheck, title: "They get verified", body: "Extra ₹1,000 bonus when a referred broker / advocate completes KYC." },
    { icon: Wallet, title: "Withdraw anytime", body: "Use balance to buy plans or withdraw to UPI/Bank (min ₹100)." },
];

const PLAN_REWARDS = [
    { plan: "Starter", icon: Sparkles, price: "₹1,000", reward: "₹200" },
    { plan: "Silver", icon: Rocket, price: "₹2,500", reward: "₹500" },
    { plan: "Gold", icon: Zap, price: "₹4,500", reward: "₹900", featured: true },
    { plan: "Platinum", icon: Crown, price: "₹6,500", reward: "₹1,300" },
];

const COMMISSION_EXAMPLES = [
    { value: "₹8 Lakh", commission: "₹5,000", slab: "Up to ₹10 L → Fixed" },
    { value: "₹20 Lakh", commission: "₹5,000", slab: "0.25% slab" },
    { value: "₹40 Lakh", commission: "₹8,000", slab: "0.20% slab" },
    { value: "₹1 Crore", commission: "₹10,000 – ₹15,000", slab: "0.10–0.15% (admin)" },
];

const FAQ = [
    { q: "Who can refer?", a: "Anyone with a Global Property Mart account — buyer, broker, advocate, or loan agent. Your referral code is generated automatically on signup." },
    { q: "How much can I earn from website referrals?", a: "₹200 signup bonus credits (non-withdrawable, redeemable on subscription plans) + ₹1,000 verification bonus (withdrawable) + plan-tier bonus (Starter ₹200 / Silver ₹500 / Gold ₹900 / Platinum ₹1,300) when your referee buys a subscription." },
    { q: "How does property reward work?", a: "Every property page has a 'Share & Earn' button. Sharing any listing attaches your referral code. When a deal closes through your link, you earn a tiered reward — ₹5,000 fixed on properties up to ₹10 L, 0.25% (₹10–25 L), 0.20% (₹25–50 L) and 0.10–0.15% (above ₹50 L, admin-controlled). Earn up to 1% on every successful deal." },
    { q: "When does reward credit?", a: "Property reward is credited once our admin verifies the deal completion (we contact both parties + the broker). Funds typically appear in your wallet within 7 business days of closing." },
    { q: "How do I withdraw earnings?", a: "Go to Dashboard → Wallet → Request Withdrawal. Minimum ₹100. Paid to your UPI/Bank within 2 business days after admin approval." },
    { q: "Are fake / self-referrals allowed?", a: "No. Self-referrals and duplicate accounts are detected automatically and bonuses are reversed. Repeated abuse leads to account suspension." },
];

export default function ReferAndEarn() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [copied, setCopied] = useState(false);
    const [board, setBoard] = useState({ leaderboard: [], stats: { total_signups: 0, total_verified: 0, total_paid_out: 0 } });
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        api.get("/referrals/leaderboard?limit=10").then((r) => setBoard(r.data));
        if (user) {
            api.get("/referrals/my-property-referrals").then((r) => setAnalytics(r.data));
        }
    }, [user]);

    const refCode = user?.referral_code || "";
    const refLink = refCode ? `${window.location.origin}/register?ref=${refCode}` : "";

    const copy = async (text, msg) => {
        const target = text || refLink;
        if (!target) return;
        try {
            await navigator.clipboard.writeText(target);
            setCopied(true);
            toast.success(msg || "Referral link copied");
            setTimeout(() => setCopied(false), 1800);
        } catch { toast.error("Could not copy"); }
    };

    const buildMsg = () => `🏡 *Global Property Mart* — India's most trusted property platform.\n\nJoin using my code *${refCode}* and we both get rewarded.\n\n${refLink}`;
    const shareWhatsApp = () => refLink && window.open(`https://wa.me/?text=${encodeURIComponent(buildMsg())}`, "_blank");
    const shareTelegram = () => refLink && window.open(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(buildMsg())}`, "_blank");
    const shareFacebook = () => refLink && window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refLink)}`, "_blank");

    return (
        <div className="min-h-screen bg-[#FAFAF7] relative overflow-hidden">
            <Seo
                title="Refer & Earn — Up to 1% Commission on Property Deals"
                description="Earn ₹200 bonus credits on signup (usable for subscriptions) + ₹1,000 withdrawable on verification + plan bonuses + 1% commission on every property you successfully refer."
                path="/refer"
            />
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(ellipse 1200px 600px at 50% -10%, rgba(218,165,32,0.18), transparent 60%), radial-gradient(ellipse 900px 500px at 100% 100%, rgba(0,38,87,0.06), transparent 60%)" }} />

            {/* HERO */}
            <section className="relative pt-20 pb-12 lg:pt-24 lg:pb-16">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gold-500/30 mb-5 shadow-sm" data-testid="refer-badge">
                                <Gift className="w-3.5 h-3.5 text-gold-600" />
                                <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-700">Refer & Earn Ecosystem</span>
                            </div>
                            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-navy-500 leading-[1.05]">
                                Refer anything.<br />
                                <span className="gold-shimmer">Earn everything.</span>
                            </h1>
                            <p className="text-base lg:text-lg text-navy-500/75 mt-5 max-w-xl leading-relaxed">
                                Earn from <strong className="text-navy-500">website referrals</strong>, <strong className="text-navy-500">plan upgrades</strong> and <strong className="text-gold-700">up to 1% rewards</strong> on every property deal closed through your link. Real cash, withdraw to UPI anytime.
                            </p>

                            {user ? (
                                <div className="mt-8 rounded-2xl bg-white border border-gold-500/40 p-5 shadow-md" data-testid="refer-code-card">
                                    <div className="label-overline mb-2">Your unique referral code</div>
                                    <div className="flex flex-col sm:flex-row items-stretch gap-2">
                                        <code className="flex-1 font-mono text-2xl font-bold text-navy-500 tracking-wider px-4 py-3 bg-[#FAFAF7] rounded-md border border-navy-500/10 text-center sm:text-left" data-testid="refer-code">{refCode || "—"}</code>
                                        <button onClick={() => copy()} data-testid="refer-copy" className="px-4 py-3 rounded-md border-2 border-gold-500/40 text-gold-700 hover:bg-gold-500/10 inline-flex items-center justify-center gap-1.5 font-semibold text-sm">
                                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                            {copied ? "Copied" : "Copy"}
                                        </button>
                                    </div>
                                    <div className="flex gap-2 mt-3" data-testid="refer-share-buttons">
                                        <ChannelBtn onClick={shareWhatsApp} color="#25D366" icon={MessageCircle} label="WhatsApp" testid="share-whatsapp" />
                                        <ChannelBtn onClick={shareTelegram} color="#0088CC" icon={Send} label="Telegram" testid="share-telegram" />
                                        <ChannelBtn onClick={shareFacebook} color="#1877F2" icon={Facebook} label="Facebook" testid="share-facebook" />
                                        <ChannelBtn onClick={() => copy(refLink, "Link copied — paste in your Instagram bio")} color="#E4405F" icon={Instagram} label="Instagram" testid="share-instagram" />
                                    </div>
                                    <p className="text-xs text-navy-500/55 mt-3 font-mono break-all">{refLink || "—"}</p>
                                </div>
                            ) : (
                                <div className="mt-8 rounded-2xl bg-white border border-navy-500/10 p-5 shadow-md">
                                    <div className="text-sm text-navy-500/80 mb-3">Sign in or create an account to get your unique referral link & start earning.</div>
                                    <div className="flex gap-2">
                                        <Link to="/register" data-testid="refer-signup-cta" className="text-on-image flex-1 text-center px-5 py-3 rounded-md bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 shadow-sm">Sign up free</Link>
                                        <Link to="/login" data-testid="refer-login-cta" className="text-on-image flex-1 text-center px-5 py-3 rounded-md bg-navy-500 text-white font-bold text-sm hover:bg-[#1a3a6e]">Login</Link>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-testid="refer-stats">
                            <StatCard icon={Gift} label="Per signup" value="₹200" />
                            <StatCard icon={ShieldCheck} label="Per verified partner" value="+₹1,000" />
                            <StatCard icon={Building2} label="Per property deal" value="Up to 1%" />
                            <StatCard icon={Wallet} label="Paid out so far" value={formatINR(board.stats.total_paid_out)} />
                        </div>
                    </div>
                </div>
            </section>

            {/* MY ANALYTICS */}
            {user && analytics && (
                <section className="relative py-12 border-y border-navy-500/8 bg-white/60 backdrop-blur-sm">
                    <div className="max-w-6xl mx-auto px-6 lg:px-8">
                        <div className="text-center mb-8">
                            <div className="label-overline mb-2">Your performance</div>
                            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy-500">Property referral analytics</h2>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <AnalyticsCard icon={Share2} label="Clicks" value={analytics.totals.total_clicks} />
                            <AnalyticsCard icon={Users} label="Inquiries" value={analytics.totals.total_inquiries} />
                            <AnalyticsCard icon={Building2} label="Deals closed" value={analytics.totals.total_conversions} />
                            <AnalyticsCard icon={Wallet} label="Rewards earned" value={formatINR(analytics.totals.total_commission_earned)} />
                        </div>
                    </div>
                </section>
            )}

            {/* GPM PARTNER PROGRAM BAND */}
            <section className="text-on-image relative py-14 lg:py-20 bg-gradient-to-br from-navy-500 via-[#0b2545] to-navy-500 text-white" data-testid="gpm-partner-band">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/15 border border-gold-500/40 mb-4">
                                <Sparkles className="w-3.5 h-3.5 text-gold-500" />
                                <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-500">GPM Partner Program</span>
                            </div>
                            <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-[1.1]">
                                Go pro with a<br /><span className="text-gold-500">GPM Partner ID.</span>
                            </h2>
                            <p className="mt-4 text-white/80 leading-relaxed max-w-xl text-[15px]">
                                A one-time <b className="text-gold-500">₹1,000 / 12-month onboarding package</b> that gives you a branded Partner ID card, marketing kit, GPM property catalogue access, and a dedicated dashboard to track your referrals, leads, transactions and eligible commission — all backed by GPM's real-estate ecosystem.
                            </p>
                            <ul className="mt-5 space-y-2 text-sm text-white/85">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5" /> GPM Partner ID (GPM-PARTNER-XXXXXX) + branded card</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5" /> WhatsApp catalogue templates + property flyer captions</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5" /> Eligible commission on qualifying property/service transactions (as per applicable tiers)</li>
                                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5" /> 7-day full-refund cooldown (if kit not downloaded and no attributed leads)</li>
                            </ul>
                            <p className="text-[11px] text-white/50 mt-3 leading-relaxed">
                                * This is a paid onboarding service, not an investment or income guarantee. Commission is based on qualifying business activity and subject to GPM verification &amp; Terms.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                {user?.role === "gpm_partner" ? (
                                    <Link to="/partner/dashboard" data-testid="partner-dashboard-cta" className="h-12 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold flex items-center gap-2">
                                        Open Partner Dashboard <ArrowRight className="w-4 h-4" />
                                    </Link>
                                ) : (
                                    <Link to="/register?role=gpm_partner" data-testid="become-partner-cta" className="h-12 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold flex items-center gap-2">
                                        Become a GPM Partner <ArrowRight className="w-4 h-4" />
                                    </Link>
                                )}
                                <Link to="/terms" className="h-12 px-6 rounded-xl border border-white/20 hover:bg-white/5 text-white font-semibold flex items-center">
                                    Read Partner Terms
                                </Link>
                            </div>
                        </div>
                        <div className="rounded-3xl bg-gradient-to-br from-gold-500/15 via-transparent to-transparent border border-gold-500/40 p-6 sm:p-8 shadow-2xl" data-testid="partner-package-preview">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500 font-extrabold">Package Preview</div>
                            <div className="font-heading text-2xl font-bold mt-2">GPM Partner — Basic</div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-heading text-5xl font-bold text-gold-500">₹1,000</span>
                                <span className="text-white/60 text-sm">/ 12 months</span>
                            </div>
                            <div className="mt-5 rounded-xl bg-white/5 border border-white/10 p-4 space-y-2 text-sm">
                                <Row label="Partner ID Card" value="✓" />
                                <Row label="Marketing Kit" value="10+ assets" />
                                <Row label="Catalogue Access" value="Full GPM" />
                                <Row label="Commission Tier" value="As per applicable" />
                                <Row label="Refund Window" value="7 days" />
                                <Row label="Support" value="Partner channel" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* GPM PARTNER BUSINESS PRESENTATION */}
            <PresentationSection />

            {/* PROPERTY COMMISSION SECTION */}
            <section className="relative py-16 lg:py-20">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30 mb-4">
                                <Building2 className="w-3.5 h-3.5 text-gold-600" />
                                <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-700">Property Rewards</span>
                            </div>
                            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-500 leading-[1.1]">
                                Refer Properties &<br /><span className="gold-shimmer">Earn Up To 1% Rewards.</span>
                            </h2>
                            <p className="text-navy-500/75 mt-4 max-w-xl leading-relaxed">
                                Refer properties and earn rewards based on successful deal value. Click <strong>Share & Earn</strong> on any property listing → send the link to potential buyers → when the deal closes, your reward is auto-credited (admin-verified).
                            </p>

                            {/* Tiered slab table */}
                            <div className="mt-5 rounded-xl bg-white border border-navy-500/10 overflow-hidden shadow-sm" data-testid="commission-tiers">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#FAFAF7] text-[10px] uppercase tracking-[0.16em] text-navy-500/55 font-bold">
                                        <tr>
                                            <th className="text-left px-4 py-3">Property value</th>
                                            <th className="text-right px-4 py-3">Reward</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-navy-500/8">
                                        <tr><td className="px-4 py-3 text-navy-500">Up to ₹10 Lakhs</td><td className="px-4 py-3 text-right font-bold text-gold-700">₹5,000 fixed</td></tr>
                                        <tr><td className="px-4 py-3 text-navy-500">₹10 L – ₹25 Lakhs</td><td className="px-4 py-3 text-right font-bold text-gold-700">0.25%</td></tr>
                                        <tr><td className="px-4 py-3 text-navy-500">₹25 L – ₹50 Lakhs</td><td className="px-4 py-3 text-right font-bold text-gold-700">0.20%</td></tr>
                                        <tr><td className="px-4 py-3 text-navy-500">Above ₹50 Lakhs</td><td className="px-4 py-3 text-right font-bold text-gold-700">0.10% – 0.15%</td></tr>
                                    </tbody>
                                </table>
                            </div>

                            <Link to="/properties" data-testid="cta-share-property" className="text-on-image inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 shadow-md">
                                <Building2 className="w-4 h-4" /> Browse properties to share
                            </Link>
                        </div>
                        <div className="text-on-image rounded-2xl bg-gradient-to-br from-[#002657] to-[#1a3a6e] p-6 lg:p-8 text-white shadow-xl" data-testid="commission-examples">
                            <div className="label-overline mb-4 text-gold-300">Live reward examples</div>
                            <div className="space-y-3">
                                {COMMISSION_EXAMPLES.map((e) => (
                                    <div key={e.value} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                                        <div>
                                            <div className="text-xs text-white/55 uppercase tracking-wider font-semibold">Property value</div>
                                            <div className="font-heading text-xl font-bold mt-0.5">{e.value}</div>
                                            <div className="text-[10px] text-white/45 mt-0.5">{e.slab}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-gold-300 uppercase tracking-wider font-semibold">You earn</div>
                                            <div className="font-heading text-2xl font-bold gold-shimmer mt-0.5">{e.commission}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-white/65 mt-4">Subject to admin verification of deal completion. Payouts via UPI/Bank within 7 business days.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PLAN TIER REWARDS */}
            <section className="relative py-14 bg-white/60 backdrop-blur-sm border-y border-navy-500/8">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <div className="label-overline mb-2">Plan upgrade bonuses</div>
                        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy-500">Bigger plan, bigger bonus</h2>
                        <p className="text-sm text-navy-500/65 mt-2">When your referee buys a subscription, you earn an instant cash reward.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {PLAN_REWARDS.map((r) => {
                            const Icon = r.icon;
                            return (
                                <div key={r.plan} className={`rounded-2xl p-6 transition-all ${
                                    r.featured ? "bg-gradient-to-br from-[#FFFBEF] to-white border-2 border-gold-500 shadow-md lg:scale-[1.03]" : "bg-white border border-navy-500/10 hover:border-gold-500/40 hover:shadow-md"
                                }`} data-testid={`plan-reward-${r.plan.toLowerCase()}`}>
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
                                        r.featured ? "bg-gold-500 text-on-image shadow-md" : "bg-gold-500/10 text-gold-600"
                                    }`}>
                                        <Icon className={`w-5 h-5 ${r.featured ? "text-white" : ""}`} />
                                    </div>
                                    <h3 className="font-heading text-xl font-bold text-navy-500">{r.plan}</h3>
                                    <p className="text-sm text-navy-500/60 mt-1">Plan price: {r.price}/mo</p>
                                    <div className="mt-4">
                                        <span className={`text-xs uppercase tracking-widest font-bold ${r.featured ? "text-gold-700" : "text-navy-500/60"}`}>You earn</span>
                                    </div>
                                    <div className={`font-heading text-4xl font-bold mt-1 ${r.featured ? "gold-shimmer" : "text-navy-500"}`}>{r.reward}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="relative py-16">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="label-overline mb-2">How it works</div>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-500">Four simple steps to start earning</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {HOW_STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div key={i} className="rounded-2xl bg-white border border-navy-500/10 p-5 hover:border-gold-500/40 hover:-translate-y-1 transition-all shadow-sm" data-testid={`refer-step-${i + 1}`}>
                                    <div className="w-11 h-11 rounded-xl bg-gold-500/10 text-gold-600 flex items-center justify-center mb-3">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gold-700">Step {i + 1}</div>
                                    <h3 className="font-heading text-lg font-bold text-navy-500 mt-1">{s.title}</h3>
                                    <p className="text-sm text-navy-500/70 mt-1.5 leading-relaxed">{s.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* LEADERBOARD */}
            <section className="relative py-16 bg-white/60 backdrop-blur-sm border-y border-navy-500/8">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30 mb-3" data-testid="leaderboard-badge">
                            <Trophy className="w-3.5 h-3.5 text-gold-600" />
                            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-700">Hall of Fame</span>
                        </div>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-500">Top Referrers</h2>
                        <p className="text-sm text-navy-500/65 mt-2">Live rankings — updated in real-time.</p>
                    </div>
                    {board.leaderboard.length === 0 ? (
                        <div className="text-center py-12 rounded-2xl bg-white border border-navy-500/10 shadow-sm" data-testid="leaderboard-empty">
                            <Trophy className="w-10 h-10 text-gold-500/40 mx-auto mb-3" />
                            <div className="text-navy-500 font-semibold">Be the first on the leaderboard!</div>
                            <p className="text-sm text-navy-500/60 mt-1">Refer your first friend and your name appears here within seconds.</p>
                        </div>
                    ) : (
                        <div className="rounded-2xl bg-white border border-navy-500/10 overflow-hidden shadow-sm" data-testid="leaderboard">
                            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 border-b border-navy-500/10 label-overline">
                                <div className="col-span-1">#</div>
                                <div className="col-span-5">User</div>
                                <div className="col-span-2 text-right">Signups</div>
                                <div className="col-span-2 text-right">Verified</div>
                                <div className="col-span-2 text-right">Earned</div>
                            </div>
                            <div className="divide-y divide-navy-500/8">
                                {board.leaderboard.map((r, i) => (
                                    <div key={r.user_id} className="grid grid-cols-12 gap-3 px-5 py-4 items-center hover:bg-[#FAFAF7]" data-testid={`leaderboard-row-${i + 1}`}>
                                        <div className="col-span-2 md:col-span-1 flex items-center gap-1">
                                            {i === 0 && <Trophy className="w-5 h-5 text-gold-500" />}
                                            {i === 1 && <Award className="w-5 h-5 text-slate-400" />}
                                            {i === 2 && <Award className="w-5 h-5 text-amber-700" />}
                                            <span className={`font-heading font-bold ${i < 3 ? "text-2xl text-navy-500" : "text-base text-navy-500/70"}`}>{i + 1}</span>
                                        </div>
                                        <div className="col-span-7 md:col-span-5 min-w-0">
                                            <div className="font-semibold text-navy-500 flex items-center gap-1.5 truncate">{r.name}{r.verified && <ShieldCheck className="w-3.5 h-3.5 text-gold-600 flex-shrink-0" />}</div>
                                            <div className="text-xs text-navy-500/55 capitalize mt-0.5">{r.role || "user"}{r.city ? ` · ${r.city}` : ""}</div>
                                        </div>
                                        <div className="col-span-1 md:col-span-2 text-right">
                                            <div className="text-base font-bold text-navy-500">{r.total_referrals}</div>
                                        </div>
                                        <div className="col-span-1 md:col-span-2 text-right">
                                            <div className="text-base font-bold text-emerald-700">{r.verified_count}</div>
                                        </div>
                                        <div className="col-span-1 md:col-span-2 text-right">
                                            <div className="text-base font-bold text-gold-700">{formatINR(r.total_earned)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* FAQ */}
            <section className="relative py-16">
                <div className="max-w-3xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <div className="label-overline mb-2">FAQ</div>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-500">Common questions</h2>
                    </div>
                    <div className="space-y-3" data-testid="refer-faq">
                        {FAQ.map((f, i) => (
                            <details key={i} className="group rounded-xl bg-white border border-navy-500/10 px-5 py-4 open:shadow-sm" data-testid={`faq-${i + 1}`}>
                                <summary className="flex items-center justify-between cursor-pointer list-none">
                                    <span className="font-semibold text-navy-500">{f.q}</span>
                                    <ArrowRight className="w-4 h-4 text-gold-600 group-open:rotate-90 transition-transform" />
                                </summary>
                                <p className="text-sm text-navy-500/75 mt-3 leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative py-20 bg-white/60 backdrop-blur-sm border-t border-navy-500/8">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <Sparkles className="w-10 h-10 text-gold-500 mx-auto mb-4" />
                    <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-500">Start earning today</h2>
                    <p className="text-navy-500/70 mt-4 max-w-2xl mx-auto">Your network is your net-worth. Share Global Property Mart with one friend today — your wallet grows tomorrow.</p>
                    {user ? (
                        <button onClick={() => copy()} data-testid="refer-bottom-cta" className="text-on-image inline-flex items-center gap-2 mt-7 px-7 py-3.5 rounded-xl bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 shadow-md">
                            <Copy className="w-4 h-4" /> Copy my referral link
                        </button>
                    ) : (
                        <Link to="/register" data-testid="refer-bottom-cta" className="text-on-image inline-flex items-center gap-2 mt-7 px-7 py-3.5 rounded-xl bg-gold-500 text-white font-bold text-sm hover:bg-gold-600 shadow-md">
                            Get your referral code <ArrowRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>
            </section>
        </div>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="rounded-2xl bg-white border border-navy-500/10 p-5 hover:border-gold-500/40 transition-colors shadow-sm">
            <div className="flex items-center justify-between">
                <Icon className="w-5 h-5 text-gold-600" />
                <TrendingUp className="w-3.5 h-3.5 text-gold-500/40" />
            </div>
            <div className="font-heading text-3xl font-bold text-navy-500 mt-3 leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-navy-500/55 mt-2 font-semibold">{label}</div>
        </div>
    );
}

function AnalyticsCard({ icon: Icon, label, value }) {
    return (
        <div className="rounded-2xl bg-white border border-navy-500/10 p-5 shadow-sm">
            <Icon className="w-5 h-5 text-gold-600" />
            <div className="font-heading text-3xl font-bold text-navy-500 mt-3 leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-navy-500/55 mt-2 font-semibold">{label}</div>
        </div>
    );
}

function ChannelBtn({ icon: Icon, label, color, onClick, testid }) {
    return (
        <button onClick={onClick} title={label} data-testid={testid} className="text-on-image flex-1 px-2 py-2 rounded-md text-white font-semibold text-xs hover:opacity-90 inline-flex items-center justify-center gap-1" style={{ background: color }}>
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
}


function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between text-white/85">
            <span className="text-white/60">{label}</span>
            <span className="font-semibold">{value}</span>
        </div>
    );
}


/* ------------------ GPM Partner Business Presentation ------------------ */
function PresentationSection() {
    const [meta, setMeta] = useState(null);
    const [open, setOpen] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);

    useEffect(() => {
        api.get("/refer/presentation/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ available: false }));
    }, []);

    if (!meta || !meta.available) return null;

    return (
        <section className="text-on-image relative py-14 lg:py-20 bg-gradient-to-br from-navy-500 via-[#0b2545] to-navy-500 text-white" data-testid="presentation-section">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/15 border border-gold-500/40 mb-4">
                            <PresentationIcon className="w-3.5 h-3.5 text-gold-500" />
                            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-gold-500">Official Deck</span>
                        </div>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-[1.1]">
                            GPM Partner Business<br /><span className="text-gold-500">Presentation.</span>
                        </h2>
                        <p className="mt-4 text-white/80 leading-relaxed max-w-xl text-[15px]">
                            Learn exactly how the GPM Partner Program works — package details, commission structure, marketing kit, referral tools and the full onboarding journey — in a professionally-designed <b className="text-gold-500">{meta.slides}-slide deck</b>.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 min-w-[140px]" data-testid="pres-stat-slides">
                                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Slides</div>
                                <div className="font-heading text-2xl font-bold text-gold-500">{meta.slides}</div>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 min-w-[140px]" data-testid="pres-stat-size">
                                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Size</div>
                                <div className="font-heading text-2xl font-bold text-white">{meta.size_mb} MB</div>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 min-w-[140px]" data-testid="pres-stat-updated">
                                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Updated</div>
                                <div className="font-heading text-lg font-bold text-white">{new Date(meta.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button onClick={() => { setIframeLoading(true); setOpen(true); }} data-testid="pres-view-btn" className="h-12 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold flex items-center gap-2 transition-colors">
                                <PresentationIcon className="w-4 h-4" /> View Presentation
                            </button>
                            <a href={meta.download_url} download={meta.filename} data-testid="pres-download-btn" className="h-12 px-6 rounded-xl border border-white/20 hover:bg-white/5 text-white font-semibold flex items-center gap-2 transition-colors">
                                <FileDown className="w-4 h-4" /> Download Presentation
                            </a>
                        </div>
                        <p className="text-[11px] text-white/50 mt-3">Free to view · No account required · Official GPM Partner Program deck</p>
                    </div>

                    <div className="relative">
                        {/* Preview card — clickable, opens the modal viewer */}
                        <button onClick={() => { setIframeLoading(true); setOpen(true); }} data-testid="pres-preview-card" className="w-full text-left group">
                            <div className="rounded-3xl border border-gold-500/40 bg-gradient-to-br from-gold-500/15 via-transparent to-transparent overflow-hidden shadow-2xl transition-transform group-hover:-translate-y-1">
                                <div className="aspect-[16/9] flex flex-col items-center justify-center bg-navy-950 relative">
                                    <img src="/gpm-logo.png" alt="GPM" className="w-24 h-24 object-contain drop-shadow-[0_0_24px_rgba(218,165,32,0.4)]" />
                                    <div className="mt-4 font-heading text-2xl font-bold text-white">Partner Program</div>
                                    <div className="mt-1 text-xs uppercase tracking-[0.22em] text-gold-500 font-extrabold">Business Presentation · {meta.slides} slides</div>
                                    <div className="absolute inset-0 flex items-center justify-center bg-navy-950/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="rounded-full bg-gold-500 text-navy-950 px-5 py-2 font-bold text-sm inline-flex items-center gap-2"><PresentationIcon className="w-4 h-4" /> Open Viewer</div>
                                    </div>
                                </div>
                                <div className="p-4 bg-navy-950/60 border-t border-gold-500/20 flex items-center justify-between">
                                    <span className="text-xs text-white/60">Click to view full deck</span>
                                    <span className="text-xs font-bold text-gold-500 inline-flex items-center gap-1">Open <ArrowRight className="w-3.5 h-3.5" /></span>
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* MODAL VIEWER — Office Online embed (works on desktop + mobile) */}
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-2 sm:p-6" onClick={() => setOpen(false)} data-testid="pres-modal">
                    <div className="w-full max-w-6xl h-[92vh] sm:h-[88vh] flex flex-col rounded-2xl bg-navy-950 border border-gold-500/40 overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-navy-900">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500 font-extrabold">GPM Partner</div>
                                <div className="font-heading text-sm sm:text-base font-bold text-white truncate">Business Presentation · {meta.slides} slides</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <a href={meta.download_url} download={meta.filename} data-testid="pres-modal-download" className="h-9 px-3 rounded-md bg-gold-500 hover:bg-gold-400 text-navy-950 text-xs font-bold inline-flex items-center gap-1.5">
                                    <FileDown className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Download</span>
                                </a>
                                <button onClick={() => setOpen(false)} data-testid="pres-modal-close" className="h-9 w-9 rounded-md bg-white/10 hover:bg-white/15 text-white inline-flex items-center justify-center"><X className="w-4 h-4" /></button>
                            </div>
                        </div>
                        <div className="relative flex-1 bg-white">
                            {iframeLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-navy-900/95 z-10">
                                    <div className="text-center">
                                        <Loader2 className="w-8 h-8 text-gold-500 animate-spin mx-auto" />
                                        <div className="mt-3 text-xs text-white/70">Loading presentation via Microsoft Office Online…</div>
                                        <div className="mt-1 text-[11px] text-white/50">This may take a few seconds on the first open</div>
                                    </div>
                                </div>
                            )}
                            <iframe
                                src={meta.embed_url}
                                title="GPM Partner Business Presentation"
                                data-testid="pres-iframe"
                                className="w-full h-full border-0"
                                onLoad={() => setIframeLoading(false)}
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
